<template>
  <div class="fullcalendar-wrapper" ref="calendar" v-loader="loading || isFetching">
    <section class="d-flex" v-if="Object.keys(calendarVisibility).length > 1">
      <div
        class="cb-container d-flex m5"
        v-for="({ color, visible }, name) in calendarVisibility"
        :key="name + key"
        @click.prevent="onVisibilityChange(name, { color, visible })"
      >
        <input
          type="checkbox"
          :checked="visible"
          :value="name"
          :name="name"
          @click="onVisibilityChange(name, { color, visible })"
          :id="name"
          class="cbx"
          style="display: none"
        />
        <label :for="name" class="toggle"
          ><span :style="{ 'background-color': visible ? color : 'white' }"></span
        ></label>
        <label :for="name" class="text-label">{{ name }}</label>
      </div>
    </section>
    <full-calendar
      :key="key"
      :date="date"
      :events="events"
      :panel-id="panelId"
      :field-id="fieldId"
      :dateFormat="dateFormat"
      :date-time-format="dateTimeFormat"
      :options="calendarOptions"
      :config="innerEntityCollection"
      :is-form-field="isFormField"
      :min-date="minDate"
      :max-date="maxDate"
      :view="currentView"
      @date-selected="onDateSelected"
      @calendar-date-changed="handleDateChange"
      @event-saved="changeEvent"
      @view-changed="changeView"
      @onCalendarInitDone="fetchData"
    />
  </div>
</template>

<script>
import moment from 'moment'
import FullCalendar from './FullCalendar'
import Loader from '../../components/Loader'
import EventBus from '../../utils/event-bus'
import listRest from '../../utils/mixin-list-rest'
import { cloneDeep, uniqBy, get, isNaN } from 'lodash'
import { CALENDAR_VIEWS, FIELD_TYPES } from '../../consts'
import { COLOR_TRANSPARENT, COLOR_SELECTED, SELECTED_CLASS } from './calendar'
/**
 * @class FullCalendarWrapper
 *
 * @description This component is a wrapper for FullCalender
 */
export default {
  name: 'FullCalendarWrapper',
  mixins: [listRest],
  components: {
    FullCalendar,
    Loader,
  },
  props: {
    entityCollection: {
      type: Array,
      default: () => [],
    },
    dateFormat: {
      type: String,
      default: 'YYYY-MM-DD',
    },
    dateTimeFormat: {
      type: String,
      default: 'YYYY-MM-DD HH:mm',
    },
    calendarOptions: {
      type: Object,
      default: () => ({}),
    },
    panelId: String,
    fieldId: String,
    fieldType: String,
    isFormField: {
      type: Boolean,
      default: false,
    },
    selectedDate: {
      type: String,
      default: '',
    },
    minDate: {
      type: String,
      default: '',
    },
    maxDate: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      key: 0,
      date: {},
      loaded: {},
      events: [],
      loading: true,
      isFetching: false,
      eventVisibility: {},
      innerEntityCollection: [],
      currentView: '',
    }
  },
  methods: {
    isDateValid(dateStr) {
      const date = new Date(dateStr)
      return date && !isNaN(date.getTime())
    },
    changeEvent(changedEvent) {
      const index = this.events.findIndex(
        (event) => event.formId === changedEvent.formId && event.id === changedEvent.id,
      )
      if (index > -1) {
        this.events[index] = changedEvent
      }
    },
    changeView({ type, from, to }) {
      if (type === this.currentView) return

      this.currentView = type
      this.date.from = from
      this.date.to = to

      this.events = []
      this.loaded = {}
      this.fetchData()
    },
    setDefaultDateBoundaries() {
      const date = this.selectedDate && this.isDateValid(this.selectedDate) ? moment(this.selectedDate) : moment()
      if (this.currentView === CALENDAR_VIEWS.MONTH) {
        this.date.from = date.clone().startOf('month').format(this.dateFormat)
        this.date.to = date.clone().add(1, 'months').startOf('month').format(this.dateFormat)
        this.date.currentDate = date.clone().format(this.dateFormat)
      } else {
        this.date.from = date.clone().startOf('week').subtract(1, 'd').format(this.dateFormat)
        this.date.to = date.clone().endOf('week').add(1, 'd').format(this.dateFormat)
        this.date.currentDate = date.clone().format(this.dateFormat)
      }
    },
    handleDateChange({ from, to }) {
      this.date.to = to
      this.date.from = from

      if (!this.loaded[this.date.from] || !this.loaded[this.date.to]) {
        this.fetchData()
      }
    },
    setDateToLoaded() {
      this.loaded[this.date.from] = true
      this.loaded[this.date.to] = true
    },
    fetchData: async function (calendar) {
      if (calendar) {
        const view = calendar.fullCalendar('getView')
        if (this.currentView === CALENDAR_VIEWS.MONTH) {
          this.date.from = get(view,'start', moment()).format(this.dateFormat)
          this.date.to = get(view,'end', moment()).format(this.dateFormat)
        }
      }

      this.isFetching = true

      const parameters = {
        panelId: this.panelId,
        fieldId: this.fieldId,
        from: this.date.from,
        to: this.date.to,
      }
      if (!this.panelId) {
        parameters.entityCollection = JSON.stringify(this.entityCollection)
      }

      if (!this.date.from && !this.date.to) {
        this.loading = false
        this.isFetching = false
        return
      }

      const { body } = await this.$client
        .execute({
          operationId: 'Calendar_getData',
          method: 'get',
          parameters,
        })
        .catch((e) => console.error(e.message))

      const { events } = body
      this.events = uniqBy(this.events.concat(events), 'id')

      this.loading = false
      this.isFetching = false
      this.setDateToLoaded()
    },
    onVisibilityChange(name, payload) {
      payload.visible = !payload.visible
      this.calendarVisibility = { [name]: payload }
      ++this.key
    },
    onDateSelected(date) {
      this.$emit('date-selected', date)
    },
    setView() {
      const storedView = window.localStorage.getItem(`${this.panelId || this.fieldId}-calendar`)
      if (storedView) {
        this.currentView = storedView
      } else {
        this.currentView = this.defaultView
      }
    },
  },
  computed: {
    calendarVisibility: {
      get() {
        return this.eventVisibility
      },
      set(newVal) {
        Object.assign(this.eventVisibility, newVal)
      },
    },
    defaultView() {
      const view = get(this.calendarOptions, 'defaultView')
      if (view) {
        return view
      } else if (!this.isFormField || this.fieldType === FIELD_TYPES.DATE) {
        return CALENDAR_VIEWS.MONTH
      }
      return CALENDAR_VIEWS.WEEK
    },
  },
  watch: {
    selectedDate: {
      immediate: true,
      handler(newValue, oldValue) {
        if (newValue !== oldValue) {
          let color
          let format
          if (this.fieldType === FIELD_TYPES.DATETIME) {
            format = this.dateTimeFormat
            color = COLOR_SELECTED
          } else {
            format = this.dateFormat
            color = COLOR_TRANSPARENT
          }
          this.events = this.events.filter((event) => !event.isSelected)

          if (!newValue || !this.isDateValid(newValue)) return
          this.events.push({
            date: newValue,
            start: moment(newValue).format(format),
            end:
              this.currentView === CALENDAR_VIEWS.MONTH
                ? moment(newValue).add(1, 'days').format(format)
                : moment(newValue).format(format),
            className: SELECTED_CLASS,
            isSelected: true,
            color,
          })
        }
      },
    },
  },
  mounted() {
    this.innerEntityCollection = cloneDeep(this.entityCollection)
    EventBus.$on('dynform-saved', this.fetchData())
    this.setView()
    this.setDefaultDateBoundaries()
  },
  beforeDestroy() {
    EventBus.$off('dynform-saved', this.fetchData())
  }
}
</script>

<style scoped>
.fullcalendar-wrapper {
  min-height: 300px;
}
</style>
